import { Page, Text, View, Document, StyleSheet } from "@react-pdf/renderer";
import moment from "moment";

const styles = StyleSheet.create({
    page: {
        fontFamily: "Helvetica",
        fontSize: 10,
        padding: 30,
        backgroundColor: "#ffffff",
        color: "#2c2c2c",
        paddingBottom: 75,
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 20,
        paddingBottom: 15,
        borderBottomWidth: 3,
        borderBottomColor: "#2c3e50",
        borderBottomStyle: "solid",
    },
    headerLeft: { flex: 1 },
    companyName: {
        fontSize: 18,
        fontWeight: "bold",
        color: "#2c3e50",
        marginBottom: 4,
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },
    companyContact: { fontSize: 8, color: "#555", lineHeight: 1.5, marginBottom: 1 },
    headerRight: { width: 180, alignItems: "flex-end" },
    invoiceTitle: {
        fontSize: 22,
        fontWeight: "bold",
        color: "#2c3e50",
        marginBottom: 8,
        letterSpacing: 1,
    },
    invoiceBox: {
        backgroundColor: "#2c3e50",
        padding: 8,
        borderRadius: 3,
        width: "100%",
    },
    invoiceLabel: { fontSize: 7, color: "#bdc3c7", marginBottom: 2, textTransform: "uppercase" },
    invoiceValue: { fontSize: 10, color: "#fff", fontWeight: "bold" },
    infoGrid: { marginBottom: 20 },
    infoRow: { flexDirection: "row", gap: 15 },
    infoBox: {
        flex: 1,
        backgroundColor: "#f8f9fa",
        padding: 12,
        borderLeftWidth: 3,
        borderLeftColor: "#3498db",
        borderLeftStyle: "solid",
        marginBottom: 12,
    },
    infoTitle: {
        fontSize: 9,
        fontWeight: "bold",
        color: "#2c3e50",
        marginBottom: 8,
        textTransform: "uppercase",
        letterSpacing: 0.3,
    },
    infoDetailRow: { flexDirection: "row", marginBottom: 4 },
    infoLabel: { fontSize: 8, color: "#7f8c8d", width: "40%" },
    infoValue: { fontSize: 8, color: "#2c3e50", width: "60%", fontWeight: "bold" },
    calcNote: { fontSize: 7, color: "#95a5a6", fontStyle: "italic", marginTop: 2 },
    table: { marginTop: 15, marginBottom: 20 },
    tableHeader: {
        flexDirection: "row",
        backgroundColor: "#34495e",
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderTopLeftRadius: 3,
        borderTopRightRadius: 3,
    },
    tableHeaderText: { color: "#ecf0f1", fontSize: 9, fontWeight: "bold", textTransform: "uppercase" },
    tableRow: {
        flexDirection: "row",
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderBottomWidth: 1,
        borderBottomColor: "#ecf0f1",
        borderBottomStyle: "solid",
    },
    tableRowAlt: { backgroundColor: "#f8f9fa" },
    tableCell: { fontSize: 8, color: "#2c3e50" },
    tableCellBold: { fontWeight: "bold" },
    tableCellCalc: { fontSize: 6.5, color: "#7f8c8d", marginTop: 2 },
    col1: { width: "70%" },
    col3: { width: "30%", textAlign: "right" },
    summarySection: { marginTop: 20, flexDirection: "row", justifyContent: "flex-end" },
    summaryBox: { width: "50%", borderTopWidth: 2, borderTopColor: "#34495e", borderTopStyle: "solid", paddingTop: 10 },
    summaryRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 5, paddingHorizontal: 12 },
    summaryLabel: { fontSize: 9, color: "#7f8c8d" },
    summaryValue: { fontSize: 9, color: "#2c3e50", fontWeight: "bold" },
    summaryTotal: {
        backgroundColor: "#2c3e50",
        flexDirection: "row",
        justifyContent: "space-between",
        paddingVertical: 10,
        paddingHorizontal: 12,
        marginTop: 5,
        borderRadius: 3,
    },
    summaryTotalLabel: { fontSize: 11, color: "#ecf0f1", fontWeight: "bold", textTransform: "uppercase" },
    summaryTotalValue: { fontSize: 12, color: "#3498db", fontWeight: "bold" },
    footer: {
        position: "absolute",
        bottom: 20,
        left: 30,
        right: 30,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingTop: 10,
        borderTopWidth: 2,
        borderTopColor: "#34495e",
        borderTopStyle: "solid",
    },
    footerText: { fontSize: 8, color: "#7f8c8d", marginBottom: 2 },
    footerBold: { fontSize: 9, color: "#2c3e50", fontWeight: "bold" },
    footerThankYou: { fontSize: 9, color: "#3498db", fontWeight: "bold", fontStyle: "italic" },
});

const fmt = (v) => Number(v || 0).toFixed(2);
const formatDate = (ts) => ts ? moment.unix(ts).format("DD MMM YYYY") : "";

const TCSInvoicePDF = ({ row, calculations, invoiceNumber }) => {
    const { tcs, gstSlab, isGstRegistered, sellRateExclGST } = calculations;
    const sellRate = row.custom_price > 0 ? row.custom_price : row.total_price;
    const gstPercent = Math.round(gstSlab * 100);
    const guestName = `${row.title || ""} ${row.guest_name || ""}`.trim() || "N/A";
    const hotelName = row.hotel_id?.name || "N/A";
    const nights = row.checkin_date && row.checkout_date
        ? moment.unix(row.checkout_date).diff(moment.unix(row.checkin_date), "days")
        : 0;

    const tcsRateLabel = isGstRegistered
        ? "1% of Rate "
        : `${gstPercent}% of Rate - 100% of Tax Amount`;
    const tcsRatePercent = isGstRegistered ? "1%" : `${gstPercent}%`;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.header} fixed>
                    <View style={styles.headerLeft}>
                        <Text style={styles.companyName}>Perfect Hands Solutions</Text>
                        <Text style={styles.companyContact}>TCS Invoice</Text>
                    </View>
                    <View style={styles.headerRight}>
                        <Text style={styles.invoiceTitle}>TCS INVOICE</Text>
                        {invoiceNumber && (
                            <View style={styles.invoiceBox}>
                                <Text style={styles.invoiceLabel}>Invoice No</Text>
                                <Text style={styles.invoiceValue}>{invoiceNumber}</Text>
                            </View>
                        )}
                        <View style={[styles.invoiceBox, invoiceNumber ? { marginTop: 5, backgroundColor: "#34495e" } : {}]}>
                            <Text style={styles.invoiceLabel}>Invoice Date</Text>
                            <Text style={styles.invoiceValue}>{new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</Text>
                        </View>
                    </View>
                </View>

                <View style={styles.infoGrid}>
                    <View style={styles.infoRow}>
                        <View style={styles.infoBox}>
                            <Text style={styles.infoTitle}>Booking Details</Text>
                            <View style={styles.infoDetailRow}>
                                <Text style={styles.infoLabel}>Guest Name:</Text>
                                <Text style={styles.infoValue}>{guestName}</Text>
                            </View>
                            <View style={styles.infoDetailRow}>
                                <Text style={styles.infoLabel}>Hotel:</Text>
                                <Text style={styles.infoValue}>{hotelName}</Text>
                            </View>
                            <View style={styles.infoDetailRow}>
                                <Text style={styles.infoLabel}>Check-in:</Text>
                                <Text style={styles.infoValue}>{formatDate(row.checkin_date)}</Text>
                            </View>
                            <View style={styles.infoDetailRow}>
                                <Text style={styles.infoLabel}>Check-out:</Text>
                                <Text style={styles.infoValue}>{formatDate(row.checkout_date)}</Text>
                            </View>
                            <View style={styles.infoDetailRow}>
                                <Text style={styles.infoLabel}>Nights:</Text>
                                <Text style={styles.infoValue}>{nights}</Text>
                            </View>
                        </View>
                        <View style={[styles.infoBox, { borderLeftColor: "#e74c3c" }]}>
                            <Text style={styles.infoTitle}>Rate & Tax Details</Text>
                            <View style={styles.infoDetailRow}>
                                <Text style={styles.infoLabel}>Rate :</Text>
                                <Text style={styles.infoValue}>{`₹${fmt(sellRateExclGST)}`}</Text>
                            </View>
                            <View style={styles.infoDetailRow}>
                                <Text style={styles.infoLabel}>GST:</Text>
                                <Text style={styles.infoValue}>{`₹${fmt(sellRate - sellRateExclGST)}`}</Text>
                            </View>
                            <View style={styles.infoDetailRow}>
                                <Text style={styles.infoLabel}>Sell Rate :</Text>
                                <Text style={styles.infoValue}>{`₹${fmt(sellRate)}`}</Text>
                            </View>
                            <View style={[styles.infoDetailRow, { marginTop: 4 }]}>
                                <Text style={styles.infoLabel}>GST Slab:</Text>
                                <Text style={styles.infoValue}>{`${gstPercent}%`}</Text>
                            </View>
                            <View style={styles.infoDetailRow}>
                                <Text style={styles.infoLabel}>Property GST Status:</Text>
                                <Text style={styles.infoValue}>{isGstRegistered ? "GST Registered" : "Non-GST"}</Text>
                            </View>
                            <View style={styles.infoDetailRow}>
                                <Text style={styles.infoLabel}>TCS Rate:</Text>
                                <Text style={styles.infoValue}>{tcsRatePercent}</Text>
                            </View>
                            <View style={styles.infoDetailRow}>
                                <Text style={styles.infoLabel}>Status:</Text>
                                <Text style={styles.infoValue}>{(row.status || "").toUpperCase()}</Text>
                            </View>
                        </View>
                    </View>
                </View>

                <View style={styles.table}>
                    <View style={styles.tableHeader}>
                        <Text style={[styles.tableHeaderText, styles.col1]}>Description</Text>
                        <Text style={[styles.tableHeaderText, styles.col3]}>Amount (₹)</Text>
                    </View>
                    <View style={[styles.tableRow, styles.tableRowAlt]}>
                        <View style={styles.col1}>
                            <Text style={styles.tableCell}>{`TCS @ ${tcsRatePercent}`}</Text>
                            <Text style={styles.tableCellCalc}>{tcsRateLabel}</Text>
                        </View>
                        <Text style={[styles.tableCell, styles.col3, styles.tableCellBold]}>{fmt(tcs)}</Text>
                    </View>
                </View>

                <View style={styles.summarySection}>
                    <View style={styles.summaryBox}>
                        <View style={styles.summaryRow}>
                            <Text style={styles.summaryLabel}>{`TCS @ ${tcsRatePercent}`}</Text>
                            <Text style={styles.summaryValue}>{fmt(tcs)}</Text>
                        </View>
                        <View style={styles.summaryTotal}>
                            <Text style={styles.summaryTotalLabel}>Total TCS</Text>
                            <Text style={styles.summaryTotalValue}>{`₹${fmt(tcs)}`}</Text>
                        </View>
                    </View>
                </View>

                <View style={styles.footer} fixed>
                    <View>
                        <Text style={styles.footerText}>Generated on: {new Date().toLocaleDateString("en-IN")}</Text>
                        <Text style={styles.footerBold}>Perfect Hands Solutions</Text>
                    </View>
                    <View style={{ alignItems: "flex-end" }}>
                        <Text style={styles.footerThankYou}>Thank You!</Text>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

export default TCSInvoicePDF;
